import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, Paper, Typography, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIcon from '@mui/icons-material/DragIndicator';
import MinimizeIcon from '@mui/icons-material/Minimize';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { nextZ, currentZ, registerMinimize } from './floatingWindowZ';

const FloatingIframeWindow = ({
  url,
  title,
  open,
  onClose,
  offsetIndex = 0,
  zIndex: zIndexProp,
  onFocus,
  // Caller-controlled initial size. Defaults keep the original wide /
  // near-full-height window for the legacy management screens; smaller
  // consumers (e.g. a single-observatory card) can pass compact values.
  width = 900,
  height = 'calc(100vh - 120px)',
}) => {
  const [localZ, setLocalZ] = useState(() => nextZ());
  const [position, setPosition] = useState({ x: 80 + offsetIndex * 30, y: 50 + offsetIndex * 30 });
  const [minimized, setMinimized] = useState(false);
  const expandedPos = useRef(null);
  const minimizedPos = useRef(null);
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const paperRef = useRef(null);

  const toggleMinimized = useCallback(() => {
    setMinimized(prev => {
      if (!prev) {
        expandedPos.current = { ...position };
        if (minimizedPos.current) setPosition(minimizedPos.current);
      } else {
        minimizedPos.current = { ...position };
        if (expandedPos.current) setPosition(expandedPos.current);
      }
      return !prev;
    });
  }, [position]);

  const handleMouseDown = useCallback((e) => {
    isDragging.current = true;
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
    const handleMouseMove = (e) => {
      if (isDragging.current) {
        setPosition({
          x: Math.max(0, e.clientX - dragOffset.current.x),
          y: Math.max(0, e.clientY - dragOffset.current.y),
        });
      }
    };
    const handleMouseUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [position]);

  // Register for minimize-all
  useEffect(() => {
    if (!open) return;
    const doMinimize = () => {
      if (!minimized) {
        expandedPos.current = position;
        if (minimizedPos.current) setPosition(minimizedPos.current);
        setMinimized(true);
      }
    };
    return registerMinimize(doMinimize);
  }, [open, minimized, position]);

  if (!open || !url) return null;

  return (
    <Paper
      ref={paperRef}
      elevation={8}
      data-floating-window="true"
      onMouseDown={() => { setLocalZ(nextZ()); if (onFocus) onFocus(); }}
      sx={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        width: minimized ? 300 : width,
        maxWidth: 'calc(100vw - 40px)',
        height: minimized ? 'auto' : height,
        minHeight: minimized ? 'auto' : 300,
        resize: minimized ? 'none' : 'both',
        zIndex: zIndexProp || localZ,
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid rgba(25,118,210,${(zIndexProp || localZ) >= currentZ() ? '0.7' : '0.35'})`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Title bar — draggable */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          backgroundColor: (zIndexProp || localZ) >= currentZ() ? 'rgba(25,118,210,0.25)' : 'rgba(25,118,210,0.12)',
          borderBottom: `1px solid rgba(25,118,210,${(zIndexProp || localZ) >= currentZ() ? '0.5' : '0.25'})`,
          cursor: 'move',
          userSelect: 'none',
          flexShrink: 0,
        }}
      >
        <DragIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.3)' }} />
        <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600, fontSize: '0.8rem' }} noWrap>
          {title || 'Window'}
        </Typography>
        <Tooltip title="Open in new tab">
          <IconButton size="small" onClick={() => window.open(url, '_blank')} sx={{ p: 0.25 }}>
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={toggleMinimized} sx={{ p: 0.25 }}>
          <MinimizeIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton size="small" onClick={onClose} sx={{ p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Content — iframe */}
      {!minimized && (
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <iframe
            src={url}
            title={title || 'Window'}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              backgroundColor: '#121212',
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default FloatingIframeWindow;
